import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Field, Formik } from "formik";
import _, { camelCase, uniqueId } from 'lodash';

import { FitinsurAPI, MessageCenter } from 'utils';
import { GlobalAuthContext, GlobalLoaderContext } from 'store/context';

import ResponsiveTable from '../ResponsiveTable';
import Button, { ButtonContainer } from '../Button';
import ComboBox from '../Form/ComboBox3';

import { TagInput, DragZoneContainer, DropZoneArea, TableContainer } from './styles';

const defaultExtensions = [
  '.doc', '.docm', '.docx', '.dot', '.dotm', '.dotx', '.htm',
  '.html', '.msg', '.pdf', '.rtf', '.txt', '.wpd', '.xhtml',
  '.xps', '.bmp', '.gif', '.heic', '.jpg', '.jpeg', '.png',
  '.tif', '.tiff', '.pot', '.potx', '.pps', '.ppt', '.pptm',
  '.pptx', '.csv', '.xls', '.xlsm', '.xlsx'
];

const DragZoneUpload = ({
  disabled,
  documentTypeRequired,
  documentType,
  fullHeight,
  autoUpload,
  idOperation,
  onSuccess,
  isDraft,
  isInternalDocument,
  options,
  s3upload,
  idPersonInsurer,
  changeShowAttachments,
  showAttachmentsState,
  changeAttachments,
  question,
  periodicityText,
  periodicityOptions,
  format,
  updateIsLoadingStatus
}) => {

  const mc = new MessageCenter();
  const fitinsur = new FitinsurAPI();

  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);

  const [isDragging, setIsDragging] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);

  const onDragOver = () => setIsDragging(true);

  const onDragEnter = () => setIsDragging(true);

  const onDragLeave = () => setIsDragging(false);

  const onDrop = () => setIsDragging(false);

  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({ onDragOver, onDragEnter, onDragLeave, onDrop, disabled: !!disabled, accept: format || defaultExtensions });

  useEffect(() => {
    if (autoUpload && acceptedFiles && acceptedFiles.length && !s3upload) uploadFiles(acceptedFiles, documentType, isInternalDocument);
    else if (autoUpload && acceptedFiles && acceptedFiles.length && s3upload) uploadS3Files(acceptedFiles, documentType, isInternalDocument);
    else if (!autoUpload && acceptedFiles && acceptedFiles.length) addPendingFiles(acceptedFiles);
  }, [acceptedFiles])

  const addPendingFiles = async (files) => {
    const fileObjects = files.map(file => ({ file, type: file.type, name: file.name, folder: "", id: uniqueId() }))
    setPendingFiles(fileObjects);
  }

  const confirmPendingFiles = async () => {
    const uploadPromises = []
    for (const pendingFile of pendingFiles) {
      if(s3upload){
        uploadPromises.push(uploadS3Files([pendingFile.file], pendingFile.documentType, pendingFile.isInternalDocument, pendingFile.folder, pendingFile.periodicity))
      } else{
        uploadPromises.push(uploadFiles([pendingFile.file], pendingFile.documentType, pendingFile.isInternalDocument, pendingFile.folder, pendingFile.periodicity))
      }
    }

    const responses = await Promise.all(uploadPromises);
    if (updateIsLoadingStatus) updateIsLoadingStatus(false);
    loading.stop();
    onSuccess && onSuccess(responses.map(v => v && v[0]).filter(v => v))
    setPendingFiles([])
  }

  const uploadFiles = async (files, documentType, isInternalDocument = false, folder = "", periodicity) => {
    try {
      if (documentTypeRequired && !documentType) throw new Error("Por favor, especifique o tipo de documento.");
      if (!files || !files[0]) throw new Error("Tipo de arquivo não surportado");

      if (updateIsLoadingStatus) updateIsLoadingStatus(true);
      loading.start();
      const formData = new FormData();

      for (const file of files) formData.append('files', file)
      formData.append('idOperation', idOperation || "");
      formData.append('idTypeDocument', documentType?.idAnswerOption || "")
      formData.append('dsTypeDocument', documentType?.dsAnswerOption || "")
      formData.append('periodicity', !periodicity || periodicity === '-' ? "" : periodicity)
      formData.append('folder', folder)

      let optionBehaviorObject = null;
      if (question && Array.isArray(question?.answerOptions) && question.answerOptions.length) {
        optionBehaviorObject = question.answerOptions.find(
          opt => opt.idAnswerOption === documentType?.idAnswerOption
        ).behaviorObject;
      }
      const { isResultDomainDocument, isResultDomainStatusDocument } = optionBehaviorObject || {};
      if (isResultDomainDocument) {
        formData.append('resultIdDomainDocument', optionBehaviorObject.resultIdDomainDocument)
      }

      if (isResultDomainStatusDocument) {
        formData.append('resultIdDomainStatusDocument', optionBehaviorObject.resultIdDomainStatusDocument)
      }

      if (isDraft) formData.append('isDraft', "true")
      if (isInternalDocument) formData.append('isInternalDocument', "true")

      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestFileUpload(formData).then(response => response.json());
      if (response.ret_code !== 200) throw new Error(response.ret_msg_details)

      if (documentTypeRequired) return response.ret_value;

      if (updateIsLoadingStatus) updateIsLoadingStatus(false);
      loading.stop();
      onSuccess && onSuccess(response.ret_value);
    } catch (err) {
      if (updateIsLoadingStatus) updateIsLoadingStatus(false);
      console.log('UploadButton - error', err);
      mc.error(err.message);
      loading.stop()
    }
  }

  const uploadS3Files = async (files, documentType, isInternalDocument = false, folder = "", periodicity) => {
    try {
      if (documentTypeRequired && !documentType) throw new Error("Por favor, especifique o tipo de documento.");
      if (!files || !files[0]) throw new Error("Tipo de arquivo não surportado");

      loading.start();
      const formData = new FormData();

      for (const file of files) formData.append('files', file)

      formData.append('idOperation', idOperation || "");
      formData.append('idTypeDocument', documentType?.idAnswerOption || "")
      formData.append('dsTypeDocument', documentType?.dsAnswerOption || "")
      formData.append('periodicity', !periodicity || periodicity === '-' ? "" : periodicity)
      formData.append('folder', folder)
      formData.append('idPersonInsurer', idPersonInsurer)

      let optionBehaviorObject = null;
      if (question && Array.isArray(question?.answerOptions) && question.answerOptions.length) {
        optionBehaviorObject = question.answerOptions.find(
          opt => opt.idAnswerOption === documentType?.idAnswerOption
        ).behaviorObject;
      }
      const { isResultDomainDocument, isResultDomainStatusDocument } = optionBehaviorObject || {};
      if (isResultDomainDocument) {
        formData.append('resultIdDomainDocument', optionBehaviorObject.resultIdDomainDocument)
      }

      if (isResultDomainStatusDocument) {
        formData.append('resultIdDomainStatusDocument', optionBehaviorObject.resultIdDomainStatusDocument)
      }

      if (isDraft) formData.append('isDraft', "true")
      if (isInternalDocument) formData.append('isInternalDocument', "true")

      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestFileUploadS3(formData).then(response => response.json());
      if (response.ret_code !== 200) throw new Error(response.ret_msg_details)

      if (documentTypeRequired) return response.ret_value;

      loading.stop();
      onSuccess && onSuccess(response.ret_value);
    } catch (err) {
      console.log('UploadButton - error', err);
      mc.error(err.message);
      loading.stop()
    }
  }

  const columns = [
    {
      title: "Nome",
      key: "name",
      minWidth: 20,
      maxWidth: 20
    },
    {
      title: "Tipo",
      key: "type",
      minWidth: 20,
      maxWidth: 20,
      component: (row) => <Field
        name={`documentType-${row.id}`}
        type="text"
        topReduce
        isRequired={false}
        component={ComboBox}
        options={options || []}
        validation={false}
        searchList={[]}
        onChange={(value) => {
          let originValue = question?.answerOptions?.find(ansOpt => ansOpt.idAnswerOption == value.idAnswerOption)

          setPendingFiles(pendingFiles.map(file => file.id !== row.id ? file : {
            ...file,
            documentType: value,
            isInternalDocument: value.behavior && (
              value.behavior.folder === "Internal Documents" ||
              value.behavior.folder === "Documentos Internos"
            ),
            folder: value.behavior?.folder || originValue?.behaviorObject?.folder,
            periodicity: periodicityOptions?.includes(value.idAnswerOption) ? periodicityText : '-'
          }))
        }}
      />
    },
    (periodicityOptions?.length ? {
      title: "Periodicidade",
      key: "periodicity",
      minWidth: 20,
      maxWidth: 20,
    } : null),
    {
      title: "Pasta",
      key: "folder",
      minWidth: 20,
      maxWidth: 20,
    },
    {
      title: "",
      key: "",
      minWidth: 1,
      maxWidth: 1,
    }
  ];

  if (changeAttachments) changeAttachments(pendingFiles);

  const changeRemoveAttachments = state => setPendingFiles(state)

  return (
    <>
      {!pendingFiles.length ?
        <DragZoneContainer isDragging={isDragging} fullHeight={fullHeight} disabled={disabled}>
          <DropZoneArea {...getRootProps({ className: 'dropzone', disabled })}>
            <p>Clique ou arraste e solte os documentos aqui para envio</p>
            <TagInput {...getInputProps()} />
          </DropZoneArea>
        </DragZoneContainer>
        :
        <TableContainer>
          <ResponsiveTable columns={columns?.filter(v => v)} data={pendingFiles} changeShowAttachments={changeShowAttachments} changeRemoveAttachments={changeRemoveAttachments} />
          <ButtonContainer style={{ justifyContent: 'flex-end' }}>
            <Button
              onClick={() => {
                setPendingFiles([])
                changeShowAttachments && changeShowAttachments(!showAttachmentsState);
              }}
              third
            >
              CANCELAR
            </Button>

            <Button
              onClick={(e) => confirmPendingFiles()}
              primary
            >
              CONFIRMAR
            </Button>
          </ButtonContainer>
        </TableContainer>
      }
    </>
  );
};


export default DragZoneUpload;
